import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  RotateCw,
  Calendar,
  HardDrive,
  View,
  OctagonAlert,
  Search,
  X,
} from "lucide-react";
import DocumentMetricsPanel from "./DocumentMetricsPanel";
import Header from "./Header";
import CONFIG from "../lib/api";

const MAX_COMPLETENESS_SCORE = 19;

// Mock API function - replace with your actual API endpoint
const fetchDocumentList = async (params = {}) => {
  const { limit = 50, offset = 0, status = null, search = null } = params;

  // Replace this URL with your actual API endpoint
  const baseUrl = CONFIG.baseUrl; // or your API URL
  let url = `${baseUrl}/documents?limit=${limit}&offset=${offset}`;

  if (status) {
    url += `&status=${encodeURIComponent(status)}`;
  }

  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    // For demo purposes, return mock data if API is not available
    console.warn("API not available, using mock data:", error);
    return [];
  }
};

const hasDateAnomaly = (document) => {
  return document.is_pdf_date_outside_policy_window;
  if (!document.creation_date || !document.policy_effective_date) {
    return false;
  }

  try {
    const creationDate = new Date(document.creation_date);
    const effectiveDate = new Date(document.policy_effective_date);

    // Return true if document was created after policy effective date
    return creationDate > effectiveDate;
  } catch (error) {
    console.warn("Error comparing dates:", error);
    return false;
  }
};

const getScoreBadgeColor = (score) => {
  if (score === null || score === undefined)
    return "bg-gray-100 text-gray-500 border-gray-200";
  if (score >= 16) return "bg-green-100 text-green-800 border-green-200";
  if (score >= 12) return "bg-yellow-100 text-yellow-800 border-yellow-200";
  return "bg-red-100 text-red-800 border-red-200";
};

const getRiskBadgeColor = (score) => {
  if (score === null || score === undefined)
    return "bg-gray-100 text-gray-500 border-gray-200";
  if (score >= 0.6) return "bg-red-100 text-red-800 border-red-200";
  if (score >= 0.3) return "bg-yellow-100 text-yellow-800 border-yellow-200";
  return "bg-green-100 text-green-800 border-green-200";
};

const DocumentList = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [activeTab, setActiveTab] = useState("all");

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showMetricsPanel, setShowMetricsPanel] = useState(false);

  // Navigation function for document selection
  const onDocumentSelect = (documentId) => {
    navigate(`/documents/${documentId}`);
  };

  useEffect(() => {
    const loadDocuments = async () => {
      setLoading(true);
      setError(null);
      try {
        // Map UI tab to API status filter
        const statusMap = {
          all: null,
          pending: "Pending",
          processed: "Processed",
          error: "Error",
        };

        // Convert to 0-based indexing for API
        const offset = (currentPage - 1) * pageSize;

        // Prepare params for API
        const params = {
          limit: pageSize,
          offset: offset,
          status: statusMap[activeTab],
          search: activeSearchQuery || null,
        };

        const data = await fetchDocumentList(params);

        if (data.success) {
          setDocuments(data.documents || []);
          setTotalCount(data.pagination?.total || 0);

          // Calculate if there's a next page
          const totalPages = Math.ceil(
            (data.pagination?.total || 0) / pageSize
          );
          setHasNextPage(currentPage < totalPages);
        } else {
          setError(data.error || "Failed to load documents");
        }
      } catch (error) {
        console.error("Error loading documents:", error);
        setError("Failed to load document list. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [currentPage, pageSize, activeTab, activeSearchQuery]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const offset = (currentPage - 1) * pageSize;
      const statusMap = {
        all: null,
        pending: "Pending",
        processed: "Processed",
        error: "Error",
      };

      const params = {
        limit: pageSize,
        offset: offset,
        status: statusMap[activeTab],
        search: activeSearchQuery || null,
      };

      const data = await fetchDocumentList(params);

      if (data.success) {
        setDocuments(data.documents || []);
        setTotalCount(data.pagination?.total || 0);

        const totalPages = Math.ceil((data.pagination?.total || 0) / pageSize);
        setHasNextPage(currentPage < totalPages);
      }
    } catch (error) {
      console.error("Error refreshing documents:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleShowMetrics = (e, document) => {
    e.stopPropagation();
    setSelectedDoc(document);
    setShowMetricsPanel(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearchQuery(searchQuery);
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setCurrentPage(1);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (dateString == null || dateString === "") return "undefined";

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(date);
    } catch (e) {
      return "Invalid date";
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown";

    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "processed":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "error":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const goToPage = (page) => {
    if (page >= 1) {
      setCurrentPage(page);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Pagination component to reuse
  const PaginationControls = ({ position = "bottom" }) => (
    <div className={`flex items-center justify-between ${position === "top" ? "mb-4" : "mt-6"}`}>
      <div className="text-sm text-gray-500">
        Page {currentPage} of {totalPages} ({totalCount} total documents)
        {activeSearchQuery && (
          <span className="ml-2 text-blue-600">
            • Filtered by: "{activeSearchQuery}"
          </span>
        )}
      </div>

      <div className="flex space-x-2">
        <button
          className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </button>

        {/* Page number indicators */}
        {totalPages > 1 && (
          <div className="flex items-center space-x-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                className={`px-2 py-1 border rounded text-sm ${
                  pageNum === currentPage
                    ? "bg-blue-500 text-white border-blue-500"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            ))}
          </div>
        )}

        <button
          className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          onClick={() => goToPage(currentPage + 1)}
          disabled={!hasNextPage}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>

      <div className="flex items-center space-x-2">
        <span className="text-sm text-gray-500">Items per page:</span>
        <select
          className="border rounded p-1 text-sm"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
    </div>
  );

  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error && documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <Header
          onRefresh={handleRefresh}
          refreshing={refreshing}
          totalCount={totalCount}
        />
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <form onSubmit={handleSearch} className="flex items-center space-x-3">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search documents by policy number, agent, carrier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
          >
            Search
          </button>
          {activeSearchQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Status Tabs */}
      <div className="flex mb-6 border-b">
        <button
          className={`px-4 py-2 ${
            activeTab === "all"
              ? "text-blue-600 border-b-2 border-blue-600 font-medium"
              : "text-gray-600 hover:text-gray-800"
          }`}
          onClick={() => {
            setActiveTab("all");
            setCurrentPage(1);
          }}
        >
          All Documents
        </button>
      </div>

      {/* Top Pagination Controls */}
      {documents.length > 0 && <PaginationControls position="top" />}

      {/* Loading indicator for subsequent pages */}
      {loading && documents.length > 0 && (
        <div className="flex justify-center my-4">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}

      {/* Document List */}
      <div className="grid grid-cols-1 gap-4">
        {documents.length === 0 ? (
          <div className="border rounded-lg p-8">
            <div className="flex flex-col items-center justify-center h-40">
              <FileText className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500 mb-4">
                {activeSearchQuery ? "No documents found matching your search" : "No documents found"}
              </p>
              <p className="text-sm text-gray-400">
                {activeSearchQuery ? `Try adjusting your search for "${activeSearchQuery}"` : "Upload documents to get started"}
              </p>
              {activeSearchQuery && (
                <button
                  onClick={clearSearch}
                  className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>
        ) : (
          documents.map((document) => (
            <div
              key={document.id}
              className="border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="p-4">
                <div className="grid grid-cols-12 gap-4">
                  {/* Document Basic Info */}
                  <div className="col-span-1">
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium">
                        {document.carrier_name}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-4">
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium">
                        {document.agent_name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium">
                        {formatDate(document.policy_effective_date)} -{" "}
                        {formatDate(document.policy_expiration_date)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium">
                        {document.policy_term_months} Months
                      </span>
                    </div>
                  </div>

                  {/* File Info */}
                  <div className="col-span-2">
                    <div className="flex items-center space-x-1 mb-1">
                      <HardDrive className="h-3 w-3 text-gray-400" />
                      <span className="text-sm font-medium">
                        {formatFileSize(document.file_size_bytes)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        {formatDate(document.creation_date)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        {formatDate(document.modification_date)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium">
                        {document.creator_software?.trim() ||
                          document.producer_software}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium">
                        {document.document_author?.trim() ||
                          document.document_author}
                      </span>
                    </div>
                  </div>

                  {/* Processing Info */}
                  <div className="col-span-3">
                    {document.processing_status === "Processed" && (
                      <>
                        {document.completeness_score !== undefined && (
                          <div className="flex items-center gap-1 flex-wrap">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${getScoreBadgeColor(
                                document.completeness_score
                              )}`}
                              title="Extraction Completeness Score"
                            >
                              Completeness Score:{" "}
                              {(
                                (document.completeness_score /
                                  MAX_COMPLETENESS_SCORE) *
                                100
                              ).toFixed(1)}
                              %
                            </span>

                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${getRiskBadgeColor(
                                document.risk_score
                              )}`}
                              title="PDF Risk Score"
                            >
                              Risk: {(document.risk_score * 100).toFixed(1)}%
                            </span>

                            {hasDateAnomaly(document) && (
                              <span
                                className="px-2 py-1 flex gap-1 rounded-full text-xs font-semibold border bg-red-100 text-white-800 border-red-200 whitespace-nowrap"
                                title="Document created after policy effective date - potential anomaly"
                              >
                                <OctagonAlert className="w-4 h-4 text-red-600" />
                                Check Policy Dates
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {document.processing_status === "Pending" && (
                      <>
                        <div className="text-sm font-medium text-yellow-600">
                          Processing...
                        </div>
                        <div className="text-sm text-gray-500">
                          Started: {formatDate(document.processing_started_at)}
                        </div>
                        <div className="flex items-center space-x-1 mt-1">
                          <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                          <span className="text-xs text-gray-400">
                            In progress
                          </span>
                        </div>

                        {hasDateAnomaly(document) && (
                          <div className="mt-1">
                            <span
                              className="px-2 py-1 rounded-full text-xs font-semibold border bg-orange-100 text-orange-800 border-orange-200 whitespace-nowrap"
                              title="Document created after policy effective date - potential anomaly"
                            >
                              ⚠️ Date Issue
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {document.processing_status === "Error" && (
                      <>
                        <div className="text-sm font-medium text-red-600">
                          Processing Failed
                        </div>

                        {hasDateAnomaly(document) && (
                          <div className="mt-1">
                            <span
                              className="px-2 py-1 rounded-full text-xs font-semibold border bg-orange-100 text-orange-800 border-orange-200 whitespace-nowrap"
                              title="Document created after policy effective date - potential anomaly"
                            >
                              ⚠️ Date Issue
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Status & Actions */}
                  <div className="col-span-2 flex items-center justify-end">
                    <div className="flex items-center space-x-2">
                      <button
                        className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded"
                        onClick={(e) => handleShowMetrics(e, document)}
                        title="View document metrics"
                      >
                        <FileText className="h-4 w-4 text-blue-600" />
                      </button>

                      <button
                        className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded"
                        onClick={() =>
                          onDocumentSelect && onDocumentSelect(document.id)
                        }
                        title="Download document"
                      >
                        <View className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom Pagination Controls */}
      {documents.length > 0 && <PaginationControls position="bottom" />}

      {/* Error Banner */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Metrics Panel */}
      <DocumentMetricsPanel
        document={selectedDoc}
        isOpen={showMetricsPanel}
        onClose={() => setShowMetricsPanel(false)}
        onViewDocument={onDocumentSelect}
      />
    </div>
  );
};

export default DocumentList;